// firebase.js - ULTRATHINK: Simple and reliable Firebase initialization
import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported } from "firebase/analytics";
import {
  initializeAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  indexedDBLocalPersistence,
} from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

console.log("🔥 ULTRATHINK: Starting Firebase initialization...");

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Use the current hostname as authDomain so OAuth redirects stay same-origin.
// This fixes signInWithRedirect on Safari 16.1+, Chrome 115+, Firefox 109+.
// See: https://firebase.google.com/docs/auth/web/redirect-best-practices
const hostname = window.location.hostname;
if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
  firebaseConfig.authDomain = hostname;
}

console.log("🔥 ULTRATHINK: Config loaded");
console.log("Project ID:", firebaseConfig.projectId);

let app = null;
let auth = null;
let db = null;
let storage = null;
let analytics = null;

try {
  // Check required config
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    throw new Error("Missing required Firebase configuration");
  }
  
  // Initialize Firebase app
  app = initializeApp(firebaseConfig);
  console.log("✅ Firebase app initialized");
  
  // PERSISTENCE IS DECLARED, NOT INHERITED. getAuth() picks a store on its own, and when the
  // one it picks is unavailable — iOS Safari restricting IndexedDB is the common case — it can
  // land on in-memory, where the session dies at the next navigation. That is invisible until
  // someone signs in, moves one page, and is anonymous again.
  //
  // The list is a fallback ORDER, tried left to right, so a browser that blocks IndexedDB
  // still gets a session that survives a reload via localStorage. browserSessionPersistence
  // is last and deliberate: a session that lasts the tab beats none at all.
  auth = initializeAuth(app, {
    persistence: [
      indexedDBLocalPersistence,
      browserLocalPersistence,
      browserSessionPersistence,
    ],
    // REQUIRED, and its absence is silent. getAuth() wires this up for you; initializeAuth
    // does not, and without it every popup and redirect call — signInWithRedirect,
    // linkWithRedirect, signInWithPopup, getRedirectResult — fails immediately. The visible
    // symptom is the sign-in button doing nothing at all, because the handler throws before
    // it can navigate. Shipped broken for ~20 minutes on 2026-09-22 by swapping getAuth()
    // for initializeAuth() to control persistence and not carrying this across with it.
    popupRedirectResolver: browserPopupRedirectResolver,
  });
  console.log("✅ Firebase Auth initialized (persistence: idb → local → session)");
  
  // Initialize Firestore
  db = getFirestore(app);
  console.log("✅ Firestore initialized");
  
  // Initialize Storage
  storage = getStorage(app);
  console.log("✅ Firebase Storage initialized");

  // Initialize Analytics (async — may not be supported in all environments)
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
      console.log("✅ Firebase Analytics (GA4) initialized");
    } else {
      console.log("ℹ️ Firebase Analytics not supported in this environment");
    }
  }).catch((err) => {
    console.warn("⚠️ Firebase Analytics init failed:", err);
  });

} catch (error) {
  console.error("❌ ULTRATHINK: Firebase initialization failed:", error);
  console.error("Error details:", {
    name: error.name,
    message: error.message,
    stack: error.stack
  });
  
  // Set defaults to prevent undefined errors
  app = null;
  auth = null;
  db = null;
  storage = null;
}

// Export status functions
export const isFirebaseInitialized = () => !!app;
export const isFirestoreAvailable = () => !!db;
export const canUseFirestore = () => {
  if (!db) {
    console.warn("⚠️ ULTRATHINK: Firestore not available");
    return false;
  }
  return true;
};

// Export instances
export { app, auth, db, storage };
export const getAnalyticsInstance = () => analytics;

console.log("🔥 ULTRATHINK: Firebase module export complete");
console.log("Exports:", { app: !!app, auth: !!auth, db: !!db, storage: !!storage });